import { Button, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@data-delivery/d3s';
import { UploadIcon } from 'lucide-react';
import { useState } from 'react';
import type { Control } from 'react-hook-form';
import { BulkUploadDialog } from '../bulk-upload-dialog';
import type { UserFormValues } from '../schemas/user-form.schema';

interface UserListsSectionProps {
  control: Control<UserFormValues>;
}

export function UserListsSection({ control }: UserListsSectionProps) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState<'ipRanges' | 'distributionList'>('distributionList');

  return (
    <div className='rounded-md border p-4 text-sm'>
      <p className='font-medium mb-2'>Lists</p>
      <p className='text-muted-foreground mb-4'>
        Optional distribution list emails and IP/network entries. You can separate values by commas
        or new lines.
      </p>
      <div className='grid grid-cols-12 gap-4'>
        <div className='col-span-12 md:col-span-6'>
          <FormField
            control={control}
            name='distributionList'
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <div className='flex items-center justify-between'>
                  <FormLabel>Distribution List</FormLabel>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      setUploadType('distributionList');
                      setUploadDialogOpen(true);
                    }}
                    className='h-7 text-xs'
                  >
                    <UploadIcon className='size-3 mr-1' />
                    Bulk Upload
                  </Button>
                </div>
                <FormControl>
                  <textarea
                    {...field}
                    rows={4}
                    placeholder={'email1@example.com\nemail2@example.com'}
                    className='w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed resize-y'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className='col-span-12 md:col-span-6'>
          <FormField
            control={control}
            name='ipWhitelist'
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <div className='flex items-center justify-between'>
                  <FormLabel>IP Whitelist</FormLabel>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      setUploadType('ipRanges');
                      setUploadDialogOpen(true);
                    }}
                    className='h-7 text-xs'
                  >
                    <UploadIcon className='size-3 mr-1' />
                    Bulk Upload
                  </Button>
                </div>
                <FormControl>
                  <textarea
                    {...field}
                    rows={4}
                    placeholder={'192.168.0.1\n10.0.0.0/8'}
                    className='w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed resize-y'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      <FormField
        control={control}
        name={uploadType === 'distributionList' ? 'distributionList' : 'ipWhitelist'}
        render={({ field }) => (
          <BulkUploadDialog
            open={uploadDialogOpen}
            onClose={() => {
              setUploadDialogOpen(false);
            }}
            type={uploadType}
            currentValue={field.value || ''}
            onApply={value => {
              field.onChange(value);
              setUploadDialogOpen(false);
            }}
          />
        )}
      />
    </div>
  );
}
